import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet-async';
import {graphql} from 'gatsby';

import {getLanguageCodeFromPathname} from '@src/i18n/utils';
import {handleExternalLinks} from '@src/utils';

import Notification from '@src/components/Notification';

import favicon from './favicons/favicon.ico';
import appleTouchIcon192 from './favicons/apple-touch-icon-192x192.png';
import appleTouchIcon180 from './favicons/apple-touch-icon-180x180.png';
import appleTouchIcon from './favicons/apple-touch-icon.png';
import OGImage from '@src/images/og-image.png';

import type {PageLayoutFragment, Site} from '@graphql-types';
import type {Languages} from '@src/i18n';

import '@src/styles/global.module.scss';
import {RUM} from './rum';

interface LayoutProps {
  children: React.ReactNode;
  languages: Languages[];
  location: {
    pathname: string;
  };
  notifications: PageLayoutFragment['allPrismicNotifications'];
  siteName: string;
  siteUrl: string;
}

export const SiteContext = React.createContext<Site | null>(null);

const Layout: React.FC<LayoutProps> = props => {
  const {
    children,
    languages,
    location: {pathname},
    notifications,
    siteName,
    siteUrl,
  } = props;

  React.useEffect(() => {
    handleExternalLinks();
  }, [pathname]);

  const initialLanguageCode = getLanguageCodeFromPathname(pathname, languages);

  // Get notification node based on language code
  const notificationResult = notifications.edges.filter(edge => {
    return edge.node.lang === initialLanguageCode;
  });
  const notificationText = notificationResult[0].node.data.text.html;
  const showNotification =
    notificationResult[0].node.data.show_hide_notification;

  return (
    <main is="x3d">
      <Helmet titleTemplate={`%s | ${siteName}`} defaultTitle={siteName}>
        <html lang={initialLanguageCode.substring(0, 2)} />
        <link rel="shortcut icon" type="image/x-icon" href={favicon} />
        <link rel="apple-touch-icon" sizes="192x192" href={appleTouchIcon192} />
        <link rel="apple-touch-icon" sizes="192x192" href={appleTouchIcon180} />
        <link rel="apple-touch-icon" href={appleTouchIcon} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${siteUrl}${OGImage}`} />
        <meta name="twitter:title" content={siteName} />
        <meta name="twitter:site" content="@devotedhealth" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <RUM />
      <ContentLink />

      {showNotification === 'Visible' && (
        <Notification data={notificationText} />
      )}
      {/* children params need to get individual ported to page layout if layout wraps page layout. */}
      {children}
    </main>
  );
};

/**
 * A link to the main content section of a page. It is present to aid assistive
 * tech. It is styled so it won't be visible. Screen readers, however, can pick
 * up on it and allow users to jump to this section.
 *
 * It is split out as its own component so the useTranslation hook will pick up
 * on the i18n instance provided by the Layout component.
 */
const ContentLink: React.FC = () => {
  const {t} = useTranslation('A11y');

  return (
    <a href="#content" className="skip-to-content">
      {t('skipToContent')}
    </a>
  );
};

export default Layout;
